import React, {
  useMemo,
  useState,
  useLayoutEffect,
  useImperativeHandle,
  forwardRef,
  useRef,
} from "react";
import ReactMapGL, {
  Source,
  Layer,
  Popup,
  FlyToInterpolator,
  NavigationControl,
  WebMercatorViewport,
} from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";

import { getMapboxAccessKey } from "../../utils/config-utility";
import { useTenantConfig } from "src/main_app/actions/tenant";

const mapboxAccessKey = getMapboxAccessKey();

const MAP_STYLE = "mapbox://styles/loweblue0510/ckkqoydb526lt17s1s5ql84of";
const MAX_ZOOM_LEVEL = 18;
// Zoom level at which the heatmap "shadow"/glow is fully gone, leaving only the
// coloured circle pointers. Lower this to hide the shadow sooner.
const HEATMAP_HIDE_ZOOM = 13;

const navControlStyle = { right: 10, top: 10 };

// Pressure colour scale (m head). Low pressure = critical = red/orange,
// high pressure = healthy = green/blue. Five evenly spaced stops are mapped
// onto the actual [min, max] range of the data so the contrast always shows.
const PRESSURE_STOPS = [
  "rgb(189,0,38)", // lowest  – most critical
  "rgb(240,124,30)",
  "rgb(254,204,92)",
  "rgb(120,198,121)",
  "rgb(35,132,196)", // highest – healthiest
];

const LEGEND_LABELS = ["Low (critical)", "", "", "", "High"];

const fallbackPosition = { longitude: 151.2093, latitude: -33.8688, zoom: 7 };

const navStyleEmpty = {
  height: 360,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#8893a8",
  fontSize: 14,
  background: "#f4f6f9",
  borderRadius: 4,
};

const getCoords = (feature) => feature?.geometry?.coordinates || [];

// Build a Mapbox "interpolate" expression that maps `value` across the data
// range onto the critical→healthy colour stops.
const buildColorExpression = (min, max) => {
  if (min === max) {
    // Single value (or one site) – just paint everything the mid colour.
    return PRESSURE_STOPS[2];
  }
  const step = (max - min) / (PRESSURE_STOPS.length - 1);
  const expr = ["interpolate", ["linear"], ["get", "value"]];
  PRESSURE_STOPS.forEach((color, i) => {
    expr.push(min + step * i, color);
  });
  return expr;
};

const PressureHeatMap = ({ data }, ref) => {
  const features = Array.isArray(data?.features) ? data.features : [];

  const { minValue, maxValue } = useMemo(() => {
    const values = features
      .map((f) => Number(f?.properties?.value))
      .filter((v) => Number.isFinite(v));
    if (!values.length) return { minValue: 0, maxValue: 0 };
    return { minValue: Math.min(...values), maxValue: Math.max(...values) };
  }, [features]);

  // Geographic bounding box of all data points, used to frame (fit) the map on
  // the area that actually contains data regardless of the tenant default.
  const dataBounds = useMemo(() => {
    const coords = features
      .map(getCoords)
      .filter(([lng, lat]) => Number.isFinite(lng) && Number.isFinite(lat));
    if (!coords.length) return null;
    const lngs = coords.map((c) => c[0]);
    const lats = coords.map((c) => c[1]);
    return {
      minLng: Math.min(...lngs),
      maxLng: Math.max(...lngs),
      minLat: Math.min(...lats),
      maxLat: Math.max(...lats),
    };
  }, [features]);

  const dataCenter = useMemo(() => {
    if (!dataBounds) return null;
    return {
      longitude: (dataBounds.minLng + dataBounds.maxLng) / 2,
      latitude: (dataBounds.minLat + dataBounds.maxLat) / 2,
    };
  }, [dataBounds]);

  const containerRef = useRef(null);
  const tenantConfig = useTenantConfig();

  // Default viewport zooms to the tenant's configured default position
  // (same behaviour as the dashboard page), falling back until config loads.
  const defaultViewport = useMemo(
    () => ({
      width: "100%",
      height: "100%",
      ...fallbackPosition,
      ...(tenantConfig?.defaultPosition || {}),
      ...(dataCenter || {}),
    }),
    [tenantConfig, dataCenter]
  );

  const [viewport, setViewport] = useState(defaultViewport);
  const [hovered, setHovered] = useState(null);
  const [selected, setSelected] = useState(null);

  // Frame the map on the data once it has loaded so the first view shows the
  // area that actually contains the points (not the tenant default position).
  useLayoutEffect(() => {
    // No data yet → fall back to the tenant default position (if any).
    if (!dataBounds) {
      if (tenantConfig?.defaultPosition) {
        setViewport((vp) => ({ ...vp, ...tenantConfig.defaultPosition }));
      }
      return;
    }

    const el = containerRef.current;
    const width = el ? el.clientWidth : 0;
    const height = el ? el.clientHeight : 0;
    const { minLng, minLat, maxLng, maxLat } = dataBounds;
    const isSinglePoint = minLng === maxLng && minLat === maxLat;

    if (width > 0 && height > 0 && !isSinglePoint) {
      try {
        const fitted = new WebMercatorViewport({ width, height }).fitBounds(
          [
            [minLng, minLat],
            [maxLng, maxLat],
          ],
          { padding: 48 }
        );
        setViewport((vp) => ({
          ...vp,
          longitude: fitted.longitude,
          latitude: fitted.latitude,
          zoom: Math.min(fitted.zoom, MAX_ZOOM_LEVEL),
        }));
        return;
      } catch (e) {
        // fall through to centre-only below
      }
    }

    // Single point or container not measurable → just centre on the data.
    setViewport((vp) => ({ ...vp, ...(dataCenter || {}) }));
  }, [tenantConfig, dataBounds, dataCenter]);

  // Clear popups whenever the underlying data (e.g. range change) shifts.
  const [dataKey, setDataKey] = useState(null);
  const currentKey = `${features.length}:${minValue}:${maxValue}`;
  if (dataKey !== currentKey) {
    setDataKey(currentKey);
    setHovered(null);
    setSelected(null);
  }

  // Allow the parent (e.g. clicking a min/max site_id in the summary table)
  // to fly the map to a specific site and open its popup.
  useImperativeHandle(
    ref,
    () => ({
      focusSite(siteId) {
        if (siteId == null || siteId === "") return;
        const feature = features.find(
          (f) => String(f?.properties?.siteId) === String(siteId)
        );
        if (!feature) return;
        const [lng, lat] = getCoords(feature);
        if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;
        setViewport((prev) => ({
          ...prev,
          longitude: lng,
          latitude: lat,
          zoom: 15,
          transitionInterpolator: new FlyToInterpolator({ speed: 1.5 }),
          transitionDuration: "auto",
        }));
        setSelected({
          longitude: lng,
          latitude: lat,
          properties: feature.properties,
        });
        setHovered(null);
      },
    }),
    [features]
  );

  const colorExpression = useMemo(
    () => buildColorExpression(minValue, maxValue),
    [minValue, maxValue]
  );

  // Heatmap weight: lower pressure contributes more intensity (critical).
  const heatmapLayer = useMemo(() => {
    const weight =
      minValue === maxValue
        ? 0.5
        : ["interpolate", ["linear"], ["get", "value"], minValue, 1, maxValue, 0];
    return {
      id: "pressure-heatmap",
      type: "heatmap",
      maxzoom: HEATMAP_HIDE_ZOOM,
      paint: {
        "heatmap-weight": weight,
        "heatmap-intensity": { stops: [[11, 1], [18, 3]] },
        "heatmap-color": [
          "interpolate",
          ["linear"],
          ["heatmap-density"],
          0,
          "rgba(33,102,172,0)",
          0.2,
          "rgb(35,132,196)",
          0.4,
          "rgb(120,198,121)",
          0.6,
          "rgb(254,204,92)",
          0.8,
          "rgb(240,124,30)",
          1,
          "rgb(189,0,38)",
        ],
        "heatmap-radius": { stops: [[11, 18], [18, 26]] },
        "heatmap-opacity": { default: 0.85, stops: [[10, 0.85], [HEATMAP_HIDE_ZOOM, 0]] },
      },
    };
  }, [minValue, maxValue]);

  // Interactive circle layer on top of the heatmap for hover/click popups.
  const circleLayer = useMemo(
    () => ({
      id: "pressure-points",
      type: "circle",
      paint: {
        "circle-radius": { stops: [[7, 2], [14, 4], [18, 7]] },
        "circle-color": colorExpression,
        "circle-stroke-width": 0,
        "circle-opacity": 0.9,
      },
    }),
    [colorExpression]
  );

  const handleHover = (event) => {
    const feature = (event.features || []).find(
      (f) => f.layer && f.layer.id === "pressure-points"
    );
    if (!feature) {
      setHovered(null);
      return;
    }
    const [lng, lat] = getCoords(feature);
    setHovered({ longitude: lng, latitude: lat, properties: feature.properties });
  };

  const handleClick = (event) => {
    const feature = (event.features || []).find(
      (f) => f.layer && f.layer.id === "pressure-points"
    );
    if (!feature) return;
    const [lng, lat] = getCoords(feature);
    setSelected({
      longitude: lng,
      latitude: lat,
      properties: feature.properties,
    });
    setHovered(null);
  };

  if (!features.length) {
    return <div style={navStyleEmpty}>No location data available</div>;
  }

  const renderPopupContent = (info) => {
    const { id, siteName, siteId, value } = info.properties || {};
    return (
      <div style={{ fontSize: 13, lineHeight: 1.5, padding: 5 }}>
        <div style={{ fontWeight: 600, marginBottom: 2 }}>
          {siteName || "Unknown site"}
        </div>
        <div style={{ color: "#667085" }}>
          <a href={`/customer/${id}/pressure-usage`} target="_blank">Site ID: {siteId}</a>
        </div>
        <div style={{ fontWeight: 600, color: "#1d2a44" }}>{value} m head</div>
      </div>
    );
  };

  return (
    <div
      ref={containerRef}
      className="pressure-heat-map"
      style={{ position: "relative", minHeight: 360, borderRadius: 4, overflow: "hidden" }}
    >
      <ReactMapGL
        {...viewport}
        width="100%"
        height="100%"
        mapStyle={MAP_STYLE}
        mapboxApiAccessToken={mapboxAccessKey}
        onViewportChange={setViewport}
        interactiveLayerIds={["pressure-points"]}
        onHover={handleHover}
        onClick={handleClick}
        maxZoom={MAX_ZOOM_LEVEL}
        attributionControl={false}
      >
        <Source type="geojson" data={data}>
          <Layer {...heatmapLayer} />
          <Layer {...circleLayer} />
        </Source>

        {hovered && !selected && (
          <Popup
            tipSize={5}
            anchor="bottom"
            longitude={hovered.longitude}
            latitude={hovered.latitude}
            closeButton={false}
            closeOnClick={false}
            offsetTop={-8}
          >
            {renderPopupContent(hovered)}
          </Popup>
        )}

        {selected && (
          <Popup
            tipSize={5}
            anchor="bottom"
            longitude={selected.longitude}
            latitude={selected.latitude}
            closeButton
            closeOnClick={false}
            onClose={() => setSelected(null)}
            offsetTop={-8}
          >
            {renderPopupContent(selected)}
          </Popup>
        )}

        <NavigationControl style={navControlStyle} />
      </ReactMapGL>

      {/* Legend */}
      <div
        style={{
          position: "absolute",
          bottom: 12,
          left: 12,
          width: "50%",
          background: "rgba(255,255,255,0.92)",
          borderRadius: 4,
          padding: "8px 10px",
          boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
          fontSize: 11,
          color: "#1d2a44",
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 4 }}>
          Effective weekly avg pressure (m head)
        </div>
        <div
          style={{
            width: "100%",
            height: 10,
            borderRadius: 2,
            background: `linear-gradient(to right, ${PRESSURE_STOPS.join(", ")})`,
          }}
        />
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}>
          <span>{LEGEND_LABELS[0]}</span>
          <span>{LEGEND_LABELS[LEGEND_LABELS.length - 1]}</span>
        </div>
      </div>
    </div>
  );
};

export default forwardRef(PressureHeatMap);
